// src/components/tables/BackofficeNegativeTable.js
import React from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Badge,
  Button,
  Table,
  TextInput,
  Select,
  Textarea,
  Pagination,
  Modal,
  Label,
  Alert,
} from "flowbite-react";
import {
  HiOutlineSearch,
  HiOutlineEye,
  HiOutlinePencilAlt,
  HiOutlineClipboardCopy,
} from "react-icons/hi";
import axios from "../../config/axiosInstance";
import { NEGATIVE } from "../../config/apiConfig";
import { format } from "date-fns";
import { formatDocument } from "../../utils/utils";
import { useApplication } from "../../contexts/ApplicationContext";

const STATUS_LABELS = {
  ABERTO: "Aguardando aprovação",
  ENVIADA: "Em andamento",
  ACEITA: "Sucesso",
  ACEITO: "Sucesso",
  PENDENTE: "Alterações pendentes",
  REJEITADA: "Rejeitada",
  REJEITADO: "Rejeitada",
  BAIXA_SOLICITADA: "Baixa solicitada",
  BAIXADA: "Baixa realizada",
  BAIXADO: "Baixa realizada",
};

const STATUS_OPTIONS = [
  { value: "ABERTO", label: STATUS_LABELS.ABERTO },
  { value: "ENVIADA", label: STATUS_LABELS.ENVIADA },
  { value: "ACEITA", label: STATUS_LABELS.ACEITA },
  { value: "PENDENTE", label: STATUS_LABELS.PENDENTE },
  { value: "REJEITADA", label: STATUS_LABELS.REJEITADA },
  { value: "BAIXA_SOLICITADA", label: STATUS_LABELS.BAIXA_SOLICITADA },
  { value: "BAIXADA", label: STATUS_LABELS.BAIXADA },
];

const brl = (v) =>
  (Number(v) || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const cx = (...xs) => xs.filter(Boolean).join(" ");
const actionWrapClass = "inline-flex items-center gap-1";
const actionBtnBase =
  "inline-flex h-8 w-8 items-center justify-center rounded-lg border border-transparent p-0 transition focus:outline-none focus:ring-2 focus:ring-offset-1";
const actionBtnVariants = {
  neutral:
    "bg-slate-100/80 text-slate-600 hover:bg-slate-200 hover:text-slate-900 focus:ring-slate-300/60",
  info: "bg-blue-100/80 text-blue-700 hover:bg-blue-200 hover:text-blue-800 focus:ring-blue-200/70",
};

const formatPhone = (ddd, phone) => {
  const d = String(ddd || "").replace(/\D/g, "");
  const p = String(phone || "").replace(/\D/g, "");
  if (!d && !p) return "";
  if (d && p) return `(${d}) ${p}`;
  return p || d;
};

const collectPhones = (detail) => {
  if (!detail) return [];
  const pairs = [
    [detail.ddd1, detail.celular1],
    [detail.ddd2, detail.celular2],
    [detail.ddd3, detail.celular3],
  ];
  const list = pairs
    .map((pair, index) => {
      const value = formatPhone(pair[0], pair[1]);
      if (!value) return null;
      return { value, label: `Celular ${index + 1}` };
    })
    .filter(Boolean);
  if (list.length === 0) {
    const value = formatPhone(detail.ddd, detail.telefone);
    if (value) list.push({ value, label: "Contato" });
  }
  return list;
};

const collectEmails = (detail) => {
  if (!detail) return [];
  const pairs = [
    [detail.email1, detail.tipoEmail1],
    [detail.email2, detail.tipoEmail2],
    [detail.email3, detail.tipoEmail3],
  ];
  const list = pairs
    .map((pair, index) => {
      const email = String(pair[0] || "").trim();
      if (!email) return null;
      const tipo = String(pair[1] || "").trim();
      return { value: email, label: tipo || `Email ${index + 1}` };
    })
    .filter(Boolean);
  if (list.length === 0 && detail.email) {
    list.push({ value: detail.email, label: "Email" });
  }
  return list;
};

const StatusBadge = ({ status, className }) => {
  const S = String(status || "").toUpperCase();
  const map = {
    ABERTO: "warning",
    PENDENTE: "warning",
    ENVIADA: "info",
    BAIXA_SOLICITADA: "info",
    ACEITA: "success",
    REJEITADA: "failure",
    BAIXADA: "gray",
    BAIXADO: "gray",
    ACEITO: "success",
    REJEITADO: "failure",
  };
  const label = STATUS_LABELS[S] || S || "—";
  return (
    <Badge
      color={map[S] || "indigo"}
      className={cx(
        "w-fit rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide shadow-sm ring-1 ring-inset ring-black/5",
        className
      )}
    >
      {label}
    </Badge>
  );
};

const copyToClipboard = async (value) => {
  if (value === null || value === undefined) return;
  const text = String(value);
  if (!text.trim()) return;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
  } catch (err) {
    // fallback below
  }
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  } catch (err) {
    // noop
  }
};

const CopyButton = ({ value, label }) => {
  const hasValue =
    value !== null && value !== undefined && String(value).trim() !== "";
  return (
    <button
      type="button"
      className={cx(
        "ml-1 inline-flex items-center rounded p-0.5 text-gray-400 transition hover:text-gray-600 hover:bg-gray-100",
        !hasValue && "cursor-not-allowed opacity-40 hover:bg-transparent"
      )}
      onClick={() => (hasValue ? copyToClipboard(value) : null)}
      disabled={!hasValue}
      title={`Copiar ${label}`}
      aria-label={`Copiar ${label}`}
    >
      <HiOutlineClipboardCopy className="h-3.5 w-3.5" />
    </button>
  );
};

function useNegativesAll({
  q,
  status,
  dateFrom,
  dateTo,
  page,
  pageSize,
  orderBy,
  orderDir,
  representativeId,
  enabled,
}) {
  return useQuery(
    [
      "negatives-all",
      {
        q,
        status,
        dateFrom,
        dateTo,
        page,
        pageSize,
        orderBy,
        orderDir,
        representativeId,
      },
    ],
    async () => {
      const params = {
        // sem representativeId: visão global (backoffice real)
        // com representativeId: escopado aos clientes abaixo do representante
        all: representativeId ? undefined : 1,
        representativeId: representativeId || undefined,
        q: q || undefined,
        status: status || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        page,
        pageSize,
        orderBy,
        orderDir,
      };
      const { data } = await axios.get(NEGATIVE.LIST, { params });
      return data;
    },
    { keepPreviousData: true, enabled }
  );
}

function useNegativeDetailAll(id, open) {
  return useQuery(
    ["negative-detail-all", id],
    async () => {
      const { data } = await axios.get(`${NEGATIVE.GET(id)}?all=1`);
      return data;
    },
    { enabled: !!id && open }
  );
}

export default function BackofficeNegativeTable() {
  const { user } = useApplication();
  const qc = useQueryClient();
  const isBackoffice = ["ADMIN", "POWER_USER"].includes(user?.permissionId);
  const isRepresentanteAdmin =
    user?.permissionId === "ADMIN" &&
    user?.Company?.companyTypeId === "REPRESENTANTE";
  const representativeId = isRepresentanteAdmin
    ? user?.Company?.id
    : undefined;

  // filtros
  const [q, setQ] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [dateFrom, setDateFrom] = React.useState("");
  const [dateTo, setDateTo] = React.useState("");

  // paginação/ordenação
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);
  const [orderBy, setOrderBy] = React.useState("id");
  const [orderDir, setOrderDir] = React.useState("DESC");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [openDetail, setOpenDetail] = React.useState(false);
  const [detailId, setDetailId] = React.useState(null);
  const [detailTab, setDetailTab] = React.useState("details");

  const syncToPage = (p) => {
    setCurrentPage(p);
    setPage(p);
  };

  const activeFiltersCount = React.useMemo(() => {
    let count = 0;
    if (q) count += 1;
    if (status) count += 1;
    if (dateFrom) count += 1;
    if (dateTo) count += 1;
    return count;
  }, [q, status, dateFrom, dateTo]);

  const clearFilters = () => {
    setQ("");
    setStatus("");
    setDateFrom("");
    setDateTo("");
    syncToPage(1);
  };

  const { data, isLoading, isError } = useNegativesAll({
    q,
    status,
    dateFrom,
    dateTo,
    page,
    pageSize,
    orderBy,
    orderDir,
    representativeId,
    enabled: isBackoffice,
  });

  const {
    data: detail,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useNegativeDetailAll(detailId, openDetail);
  const detailPhones = React.useMemo(() => collectPhones(detail), [detail]);
  const detailEmails = React.useMemo(() => collectEmails(detail), [detail]);

  const [savingIds, setSavingIds] = React.useState(new Set());
  const isSaving = (id) => savingIds.has(id);
  const [openStatus, setOpenStatus] = React.useState(false);
  const [statusId, setStatusId] = React.useState(null);
  const [statusValue, setStatusValue] = React.useState("");
  const [statusObservation, setStatusObservation] = React.useState("");

  const changeStatus = useMutation(
    async ({ id, status: nextStatus, observacao }) => {
      const { data } = await axios.patch(
        `${NEGATIVE.CHANGE_STATUS(id)}?all=1`,
        { status: nextStatus, observacao: observacao || undefined }
      );
      return data;
    },
    {
      onSuccess: (_data, variables) => {
        qc.invalidateQueries("negatives-all");
        if (variables?.id) {
          qc.invalidateQueries(["negative-detail-all", variables.id]);
        }
      },
    }
  );

  const handleSaveStatus = async (id, nextStatus, observacao) => {
    setSavingIds((prev) => new Set(prev).add(id));
    try {
      await changeStatus.mutateAsync({ id, status: nextStatus, observacao });
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const totalPages = data?.totalPages || 1;

  const openDetailModal = (id) => {
    setDetailId(id);
    setDetailTab("details");
    setOpenDetail(true);
  };

  const openStatusModal = (id, currentStatus) => {
    const next =
      String(currentStatus || "").toUpperCase() || STATUS_OPTIONS[0]?.value;
    setStatusId(id);
    setStatusValue(next);
    setStatusObservation("");
    setOpenStatus(true);
  };

  const handleConfirmStatus = async () => {
    if (!statusId || !statusValue) return;
    await handleSaveStatus(statusId, statusValue, statusObservation);
    setOpenStatus(false);
  };

  const statusHistory = React.useMemo(() => {
    const raw =
      detail?.statusHistory ||
      detail?.historicoStatus ||
      detail?.statusHistorico ||
      detail?.statusHistoryEntries;
    return Array.isArray(raw) ? raw : [];
  }, [detail]);

  const resolveHistoryUser = (entry) => {
    if (!entry) return "—";
    return (
      entry?.user?.name ||
      entry?.user?.fullName ||
      entry?.usuario?.name ||
      entry?.usuario?.nome ||
      entry?.responsavel ||
      entry?.responsavelNome ||
      entry?.responsavelName ||
      "—"
    );
  };

  const resolveHistoryDate = (entry) =>
    entry?.createdAt || entry?.date || entry?.data || entry?.created_on;

  const resolveHistoryObservation = (entry) =>
    entry?.observacao || entry?.observation || entry?.note || entry?.notes || "";

  if (!isBackoffice) {
    return (
      <div className="p-4">
        <Alert color="failure">
          Você não tem permissão para acessar esta área.
        </Alert>
      </div>
    );
  }

  return (
    <div className="mx-auto">
      <div className="mb-6 flex justify-between gap-2 md:items-center">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
            Painel de Negativações
          </h1>
          <p className="text-sm text-gray-500">
            Visão global do sistema com ajuste de status.
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="mb-4 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-3 py-2 border-b border-slate-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Filtros
            </span>
            {activeFiltersCount > 0 && (
              <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                {activeFiltersCount} ativo(s)
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="xs"
              className="bg-secondary min-w-24"
              onClick={() => syncToPage(1)}
              isProcessing={isLoading}
            >
              <div className="flex items-center gap-1">
                <HiOutlineSearch /> Buscar
              </div>
            </Button>
            <Button
              color="light"
              size="xs"
              onClick={clearFilters}
              disabled={activeFiltersCount === 0}
            >
              Limpar
            </Button>
          </div>
        </div>

        <div className="px-3 py-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-3 rounded-xl border border-slate-200 bg-slate-50/40 p-3">
            <div className="rounded-xl border border-slate-200 bg-white/90 p-3 shadow-sm xl:col-span-3">
              <Label className="mb-1 block text-[13px] font-medium text-slate-600">
                Busca
              </Label>
              <TextInput
                icon={HiOutlineSearch}
                placeholder="Nome, documento, contrato..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>

            <div className="rounded-xl border border-slate-200 bg-white/90 p-3 shadow-sm">
              <Label className="mb-1 block text-[13px] font-medium text-slate-600">
                Status
              </Label>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">Todos</option>
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white/90 p-3 shadow-sm">
              <Label className="mb-1 block text-[13px] font-medium text-slate-600">
                Venc. de
              </Label>
              <TextInput
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>

            <div className="rounded-xl border border-slate-200 bg-white/90 p-3 shadow-sm">
              <Label className="mb-1 block text-[13px] font-medium text-slate-600">
                Venc. até
              </Label>
              <TextInput
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
          <div>
            <div className="text-sm text-gray-600">
              Total: <span className="font-semibold">{data?.total || 0}</span>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <Table hoverable className="text-sm">
            <Table.Head className="bg-slate-50">
              <Table.HeadCell className="text-[11px] uppercase tracking-wider text-slate-500">
                #
              </Table.HeadCell>
              <Table.HeadCell className="text-[11px] uppercase tracking-wider text-slate-500 text-center">
                Criada em
              </Table.HeadCell>
              <Table.HeadCell className="text-[11px] uppercase tracking-wider text-slate-500">
                Empresa
              </Table.HeadCell>
              <Table.HeadCell className="text-[11px] uppercase tracking-wider text-slate-500">
                Devedor
              </Table.HeadCell>
              <Table.HeadCell className="text-[11px] uppercase tracking-wider text-slate-500 text-center">
                Birôs
              </Table.HeadCell>
              <Table.HeadCell className="text-[11px] uppercase tracking-wider text-slate-500 text-center">
                Status
              </Table.HeadCell>
              <Table.HeadCell className="text-[11px] uppercase tracking-wider text-slate-500 text-center">
                Ações
              </Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {(data?.items || []).map((it) => {
                const originalStatus = String(it?.status || "").toUpperCase();
                const companyName =
                  it.Company?.socialReason || it.Company?.name || "—";
                const companyDoc = it.Company?.document
                  ? formatDocument(it.Company.document)
                  : "—";

                return (
                  <Table.Row key={it.id} className="bg-white hover:bg-slate-50">
                    <Table.Cell className="py-2 font-medium text-gray-900">
                      {it.id}
                    </Table.Cell>
                    <Table.Cell className="py-2 text-center">
                      {it.createdAt
                        ? format(new Date(it.createdAt), "dd/MM/yyyy")
                        : "—"}
                    </Table.Cell>
                    <Table.Cell className="py-2 align-middle">
                      <div className="font-medium">{companyName}</div>
                      <div className="text-xs text-gray-500">{companyDoc}</div>
                    </Table.Cell>
                    <Table.Cell className="py-2 font-medium">
                      <div className="text-sm">{it.nome || "—"}</div>
                      <div className="text-xs text-gray-500">
                        {formatDocument(it.documentoDevedor)}
                      </div>
                    </Table.Cell>

                    <Table.Cell className="py-2 font-medium text-center">
                      <div className="text-sm">
                        {it.bureaus ? it.bureaus.join(", ") : "—"}
                      </div>
                    </Table.Cell>

                    <Table.Cell className="py-2 text-center">
                      <StatusBadge status={it.status} className="mx-auto" />
                    </Table.Cell>
                    <Table.Cell className="py-2 mx-auto text-center">
                      <div className={cx("flex items-center justify-center", actionWrapClass)}>
                        <Button
                          size="xs"
                          color="light"
                          onClick={() => openDetailModal(it.id)}
                          className={cx(actionBtnBase, actionBtnVariants.neutral)}
                        >
                          <div className="flex items-center gap-1 justify-center">
                            <HiOutlineEye className="h-4 w-4" />
                          </div>
                        </Button>
                        <Button
                          size="xs"
                          onClick={() => openStatusModal(it.id, originalStatus)}
                          disabled={isSaving(it.id)}
                          color="light"
                          className={cx(actionBtnBase, actionBtnVariants.info)}
                          title="Alterar status"
                          aria-label="Alterar status"
                        >
                          <HiOutlinePencilAlt className="h-4 w-4" />
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                );
              })}

              {!isLoading && (data?.items || []).length === 0 && (
                <Table.Row>
                  <Table.Cell
                    colSpan={6}
                    className="text-center text-gray-500 py-8"
                  >
                    Nenhum registro encontrado.
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>

        {isError && (
          <div className="mt-3">
            <Alert color="failure">Erro ao carregar negativações.</Alert>
          </div>
        )}

        <div className="flex items-center justify-center mt-4">
          <Pagination
            layout="pagination"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(p) => syncToPage(p)}
            previousLabel=""
            nextLabel=""
            showIcons
          />
        </div>
      </div>

      <Modal
        show={openStatus}
        size="lg"
        onClose={() => setOpenStatus(false)}
        className="backdrop-blur-sm"
      >
        <Modal.Header className="py-2">
          <div className="text-base font-semibold">Alterar status</div>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <Label className="mb-1 block text-sm">Status</Label>
              <Select
                value={statusValue}
                onChange={(e) => setStatusValue(e.target.value)}
                disabled={isSaving(statusId)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label className="mb-1 block text-sm">Observação (opcional)</Label>
              <Textarea
                rows={3}
                value={statusObservation}
                onChange={(e) => setStatusObservation(e.target.value)}
                placeholder="Detalhes adicionais sobre a mudança de status"
                disabled={isSaving(statusId)}
              />
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button
                color="light"
                onClick={() => setOpenStatus(false)}
                disabled={isSaving(statusId)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmStatus}
                isProcessing={isSaving(statusId)}
                disabled={!statusValue || isSaving(statusId)}
              >
                Salvar
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={openDetail}
        size="5xl"
        onClose={() => setOpenDetail(false)}
        className="backdrop-blur-sm"
      >
        <Modal.Header className="py-2">
          <div className="flex items-center gap-2 mt-1">
            <div>
              <div className="text-base font-semibold">
                {detail
                  ? `#${detail.id} — ${detail.nome || "—"}`
                  : "Carregando..."}
              </div>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="pt-2 bg-gray-50">
          {isDetailLoading && (
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 animate-pulse rounded w-1/3" />
              <div className="h-24 bg-gray-100 animate-pulse rounded" />
              <div className="h-40 bg-gray-100 animate-pulse rounded" />
            </div>
          )}

          {!isDetailLoading && isDetailError && (
            <Alert color="failure">
              Não foi possível carregar os detalhes.
            </Alert>
          )}

          {!isDetailLoading && detail && (
            <div>
              <div className="mb-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setDetailTab("details")}
                  className={cx(
                    "inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition",
                    detailTab === "details"
                      ? "bg-slate-900 text-white shadow-sm"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  )}
                >
                  Detalhes
                </button>
                <button
                  type="button"
                  onClick={() => setDetailTab("history")}
                  className={cx(
                    "inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition",
                    detailTab === "history"
                      ? "bg-slate-900 text-white shadow-sm"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  )}
                >
                  Histórico
                </button>
              </div>

              <div className={detailTab === "details" ? "" : "hidden"}>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Resumo
                  </div>
                  <div className="mt-2 rounded-lg px-3 py-3 shadow-sm border border-gray-200 bg-white">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <span>Status</span>
                        </div>
                        <div className="mt-1">
                          <StatusBadge status={detail.status} />
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <span>Valor</span>
                        </div>
                        <div className="mt-1 text-lg font-semibold text-emerald-700">
                          {brl(detail.valor)}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <span>Vencimento</span>
                        </div>
                        <div className="mt-1 text-lg font-semibold text-indigo-700">
                          {detail.dataVencimento
                            ? format(
                                new Date(detail.dataVencimento),
                                "dd/MM/yyyy"
                              )
                            : "—"}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <span>Birôs</span>
                        </div>
                        <div className="mt-1 text-lg font-semibold text-blue-700">
                          {detail.bureaus ? detail.bureaus.join(", ") : "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 space-y-4">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Devedor
                    </div>
                    <div className="mt-2 rounded-lg px-3 py-3 shadow-sm border border-gray-200 bg-white">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <span>Nome</span>
                            <CopyButton value={detail.nome} label="Nome" />
                          </div>
                          <div className="font-medium">
                            {detail.nome || "—"}
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <span>Documento</span>
                            <CopyButton
                              value={
                                detail.documentoDevedor
                                  ? formatDocument(detail.documentoDevedor)
                                  : ""
                              }
                              label="Documento"
                            />
                          </div>
                          <div className="font-mono">
                            {formatDocument(detail.documentoDevedor)}
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <span>Contrato</span>
                            <CopyButton
                              value={detail.contrato}
                              label="Contrato"
                            />
                          </div>
                          <div className="font-medium">
                            {detail.contrato || "—"}
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <span>Natureza (código)</span>
                            <CopyButton
                              value={detail.codigoNatureza}
                              label="Natureza (código)"
                            />
                          </div>
                          <div className="font-medium">
                            {detail.codigoNatureza || "—"}
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <span>Participação</span>
                            <CopyButton
                              value={
                                detail?.participacao === "P"
                                  ? "Devedor Principal"
                                  : "Avalista"
                              }
                              label="Participação"
                            />
                          </div>
                          <div className="font-medium">
                            {detail?.participacao === "P"
                              ? "Devedor Principal"
                              : "Avalista" || "—"}
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <span>Criado em</span>
                            <CopyButton
                              value={
                                detail.createdAt
                                  ? format(
                                      new Date(detail.createdAt),
                                      "dd/MM/yyyy HH:mm"
                                    )
                                  : ""
                              }
                              label="Criado em"
                            />
                          </div>
                          <div className="font-medium">
                            {detail.createdAt
                              ? format(
                                  new Date(detail.createdAt),
                                  "dd/MM/yyyy HH:mm"
                                )
                              : "—"}
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <span>Atualizado em</span>
                            <CopyButton
                              value={
                                detail.updatedAt
                                  ? format(
                                      new Date(detail.updatedAt),
                                      "dd/MM/yyyy HH:mm"
                                    )
                                  : ""
                              }
                              label="Atualizado em"
                            />
                          </div>
                          <div className="font-medium">
                            {detail.updatedAt
                              ? format(
                                  new Date(detail.updatedAt),
                                  "dd/MM/yyyy HH:mm"
                                )
                              : "—"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Endereço
                    </div>
                    <div className="mt-2 rounded-lg px-3 py-3 shadow-sm border border-gray-200 bg-white">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <span>Logradouro</span>
                            <CopyButton
                              value={detail.endereco}
                              label="Logradouro"
                            />
                          </div>
                          <div className="font-medium">
                            {detail.endereco || "—"}
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <span>Número</span>
                            <CopyButton value={detail.numero} label="Número" />
                          </div>
                          <div className="font-medium">
                            {detail.numero || "—"}
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <span>Bairro</span>
                            <CopyButton value={detail.bairro} label="Bairro" />
                          </div>
                          <div className="font-medium">
                            {detail.bairro || "—"}
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <span>Complemento</span>
                            <CopyButton
                              value={detail.complemento}
                              label="Complemento"
                            />
                          </div>
                          <div className="font-medium">
                            {detail.complemento || "—"}
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <span>Cidade</span>
                            <CopyButton value={detail.cidade} label="Cidade" />
                          </div>
                          <div className="font-medium">
                            {detail.cidade || "—"}
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <span>UF</span>
                            <CopyButton value={detail.uf} label="UF" />
                          </div>
                          <div className="font-medium">{detail.uf || "—"}</div>
                        </div>
                        <div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <span>CEP</span>
                            <CopyButton value={detail.cep} label="CEP" />
                          </div>
                          <div className="font-medium">{detail.cep || "—"}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Contatos
                    </div>
                    <div className="mt-2 rounded-lg px-3 py-3 shadow-sm border border-gray-200 bg-white">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <span>Celulares</span>
                            <CopyButton
                              value={detailPhones
                                .map((p) => p.value)
                                .join(", ")}
                              label="Celulares"
                            />
                          </div>
                          <div className="mt-2 space-y-2">
                            {detailPhones.length > 0 ? (
                              detailPhones.map((p, idx) => (
                                <div
                                  key={`${p.value}-${idx}`}
                                  className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm"
                                >
                                  <span className="font-medium">{p.value}</span>
                                  <span className="text-[10px] uppercase tracking-wide text-slate-500">
                                    {p.label}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <div className="text-sm text-gray-500">—</div>
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <span>Emails</span>
                            <CopyButton
                              value={detailEmails
                                .map((e) => e.value)
                                .join(", ")}
                              label="Emails"
                            />
                          </div>
                          <div className="mt-2 space-y-2">
                            {detailEmails.length > 0 ? (
                              detailEmails.map((e, idx) => (
                                <div
                                  key={`${e.value}-${idx}`}
                                  className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm"
                                >
                                  <span className="font-medium">{e.value}</span>
                                  <span className="text-[10px] uppercase tracking-wide text-slate-500">
                                    {e.label}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <div className="text-sm text-gray-500">—</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className={detailTab === "history" ? "" : "hidden"}>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Histórico de status
                </div>
                <div className="mt-2 rounded-lg px-3 py-3 shadow-sm border border-gray-200 bg-white">
                  {statusHistory.length > 0 ? (
                    <div className="space-y-3">
                      {statusHistory.map((entry, idx) => {
                        const date = resolveHistoryDate(entry);
                        const statusLabel =
                          entry?.status || entry?.statusNovo || entry?.novoStatus;
                        const observation = resolveHistoryObservation(entry);
                        return (
                          <div
                            key={entry?.id || `${date || "hist"}-${idx}`}
                            className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2 text-sm"
                          >
                            <div className="flex flex-wrap items-center gap-2">
                              <StatusBadge status={statusLabel} />
                              <span className="text-xs text-slate-500">
                                {date
                                  ? format(new Date(date), "dd/MM/yyyy HH:mm")
                                  : "—"}
                              </span>
                              <span className="text-xs text-slate-500">
                                {resolveHistoryUser(entry)}
                              </span>
                            </div>
                            {observation && (
                              <div className="mt-2 text-xs text-slate-600">
                                {observation}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">
                      Sem histórico disponível.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
